import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert, 
  Divider,
  Chip
} from '@mui/material';
import axios from 'axios';

const InterviewStatus = () => {
  const { publicToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assessments/public/interview-status/${publicToken}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch interview status');
      } finally {
        setLoading(false);
      }
    };

    if (publicToken) {
      fetchStatus();
    }
  }, [publicToken]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'in_progress':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!data) return null;

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
          Interview Status
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Tracking for Interview Token: {publicToken.substring(0, 8)}...
        </Typography>
        
        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Status
          </Typography>
          <Chip 
            label={data.status?.toUpperCase() || 'UNKNOWN'} 
            color={getStatusColor(data.status)}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Type
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {data.type?.replace(/_/g, ' ') || 'N/A'}
          </Typography>
        </Box>

        {data.scheduledAt && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Scheduled At
            </Typography>
            <Typography variant="body1">
              {new Date(data.scheduledAt).toLocaleString()}
            </Typography>
          </Box>
        )}

        {data.completedAt && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Completed At
            </Typography>
            <Typography variant="body1">
              {new Date(data.completedAt).toLocaleString()}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
        
        <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block' }}>
          This is a public status page. No sensitive information is displayed.
        </Typography>
      </Paper>
    </Container>
  );
};

export default InterviewStatus;
